<script setup lang="ts">
import { ref } from "vue";
import Product from "~/models/Product";
import { formatPrice } from "@/utils/pricing";
import type OfferMonthDetail from "~/models/OfferMonthDetail";
import SafeHtml from "@/components/utils/SafeHtml.vue";
import { breakpointsBootstrapV5, useBreakpoints } from "@vueuse/core";

const props = defineProps({
  product: {
    type: Product,
    required: true,
  },
  showUsedPrice: Boolean,
});
const ssrWidth = useResponsiveSsrWidth();
const breakpoints = useBreakpoints(breakpointsBootstrapV5, { ssrWidth });
const isMobileSize = breakpoints.smallerOrEqual("md");
const selectedMonth = ref<OfferMonthDetail | null>(null);
const bestMonth = props.product.offer_months.slice(-1).pop();

const selectMonth = (month: OfferMonthDetail): void => {
  selectedMonth.value = month;
};

if (bestMonth) {
  selectMonth(bestMonth);
}
</script>

<template>
  <section>
    <div class="mb-2 d-flex align-items-center gap-3 fw-bold">
      <span class="text-secondary fs-6 fw-light">{{
        product.hasDiscount() ? "Normal" : "Precio"
      }}</span>
      <safe-html
        tag="span"
        :class="[
          product.hasDiscount() ? 'text-primary fs-5' : 'fs-2 price',
          showUsedPrice && !product.hasDiscount()
            ? 'text-danger'
            : 'text-muted',
        ]"
        :html="
          formatPrice(
            product.getFinalCommonPrice(),
            true,
            product.hasDiscount() ? 'text-decoration-line-through' : '',
          )
        "
      />
    </div>
    <div class="mb-3">
      <h3
        v-if="product.hasDiscount()"
        class="d-flex align-items-center gap-3 mb-0"
      >
        <span class="text-secondary fs-6 fw-light text-truncate">{{
          isMobileSize ? "Promoción" : "Promoción contado"
        }}</span>
        <safe-html
          class="text-secondary fs-2 fw-semibold price"
          tag="span"
          :class="[showUsedPrice ? 'text-info' : 'text-danger']"
          :html="formatPrice(product.getFinalCurrentPrice())"
        />
        <span
          class="badge rounded-pill bg-light text-secondary fw-normal cheaper-btn d-flex align-items-center"
        >
          Lo encontré más barato &nbsp;&nbsp;
          <i class="bi bi-plus-circle fs-6"></i>
        </span>
      </h3>
      <div v-if="product.mwi_one_disc_percent > 0.1" class="text-danger fs-6">
        ¡Con descuento a un pago!
      </div>
      <div
        v-else-if="product.mwi_wallet_percent > 0.1"
        class="text-danger fs-6"
      >
        ¡<safe-html
          tag="span"
          :html="formatPrice(product.mwi_wallet_amount_with_tax)"
        />
        en monedero a un pago!
      </div>
    </div>
    <section
      v-if="selectedMonth"
      class="d-flex align-items-center mb-4 flex-wrap gap-1"
    >
      <div class="col-md-auto">
        <div class="fw-light text-secondary small">Promoción a meses</div>
        <safe-html
          class="fw-semibold fs-6 text-secondary"
          tag="span"
          :html="formatPrice(selectedMonth.total)"
        />
      </div>
      <ul class="d-flex month-list mx-2 mx-md-3 gap-1 gap-md-3">
        <li
          v-for="month in product.offer_months"
          :key="month.months"
          class="border border-light-subtle small d-flex align-items-center justify-content-center"
          :class="{ active: selectedMonth.months == month.months }"
          @click="selectMonth(month)"
        >
          {{ month.months }}
        </li>
      </ul>
      <div class="mt-2 mt-md-0 lh-1">
        <div class="text-start">
          <span class="small fw-semibold">De&nbsp;</span>
          <safe-html
            class="small fw-semibold"
            tag="span"
            :html="formatPrice(selectedMonth.month_price)"
          />
        </div>
        <span
          v-if="
            bestMonth &&
            bestMonth.months > 0 &&
            bestMonth.interest_percent === 0
          "
          class="fw-light text-secondary small-xs text-end"
        >
          * Aplica MSI
        </span>
      </div>
    </section>
  </section>
</template>

<style lang="scss" scoped>
.cheaper-btn {
  font-size: 0.725rem;
  padding: 0.35rem 0.6rem;
  cursor: pointer;
}
.month-list {
  list-style-type: none;
  padding: 0;
  margin: 0;
  li {
    width: 32px;
    height: 32px;
    cursor: pointer;
    &.active {
      background-image: linear-gradient(
        to bottom,
        var(--bs-gray-200),
        var(--bs-white)
      );
    }
  }
}
</style>
